/**
 * Controls panel — Kanawatsugi Visual Machine (SPEC §7).
 *
 * Builds a bespoke control panel with hand-crafted DOM. Reads/writes the
 * application store directly for state owned by `AppState`. For UI-local
 * state that does not live in the store yet (`resolution`, `recording`),
 * we keep module-scope state and expose getters/setters via the returned
 * `ControlsHandle` so Wave 4 can mirror them into the store later.
 *
 * Side-effecting actions (PNG export, WebM toggle, physics reset) are
 * provided via the injected `ControlActions` interface — this module does
 * NOT import any of `src/capture/`, `src/render/`, or `src/physics/`.
 */
import { store, type AppState } from '../state.js';
import type {
  AspectId,
  ChannelId,
  HalftoneMode,
  HalftoneShape,
  ResolutionPreset,
  TitleOrientation,
} from '../types.js';

export interface ControlActions {
  /** Re-roll seed and reset blobs. */
  reset(): void;
  /** Trigger a PNG export at the current resolution. */
  exportPng(): Promise<void>;
  /** Toggle WebM recording. */
  toggleRecording(): Promise<void>;
  /** Push the current hue picker palette onto every existing blob. */
  applyHueColors(): void;
  /** Optional: show a transient notification. */
  notify?(message: string, kind?: 'info' | 'error'): void;
  /** Optional: live elapsed seconds while recording (UI polls this). */
  getRecordingElapsedSeconds?(): number;
}

/**
 * Phase 3 — per-channel L/C used for the hue slider track gradient and
 * swatch preview. Picked from the mid-point of each channel's range
 * (see `src/color/channels.ts`). Hardcoded here so the UI module stays
 * independent of the colour pipeline; if the channel ranges change
 * meaningfully these should be revisited.
 *
 * Mono is included for completeness but the picker is hidden when
 * Mono is selected (hue is invisible at C ≈ 0).
 */
const CHANNEL_HUE_TRACK: Record<ChannelId, { l: number; c: number }> = {
  ch1: { l: 0.65, c: 0.14 },
  ch2: { l: 0.65, c: 0.26 },
  ch3: { l: 0.675, c: 0.07 },
  mono: { l: 0.6, c: 0.01 },
};

export type UnmountFn = () => void;

export interface ControlsHandle {
  unmount: UnmountFn;
  getResolution(): ResolutionPreset;
  setRecording(recording: boolean): void;
}

interface ChannelOption {
  id: ChannelId;
  label: string;
}

interface AspectOption {
  id: AspectId;
  label: string;
}

interface ResolutionOption {
  id: ResolutionPreset;
  label: string;
}

type SliderKey =
  | 'objectSize'
  | 'attraction'
  | 'bgOpacity'
  | 'halftoneCellSize'
  | 'halftoneGap'
  | 'titleSize';

interface SliderConfig {
  key: SliderKey;
  label: string;
  min: number;
  max: number;
  step: number;
  decimals: number;
  /** Optional unit suffix on the readout (e.g. "px", "%"). */
  unit?: string;
  /**
   * Optional readout scaler — value × scale is displayed (e.g. internal
   * 0..1 displayed as 0..100% with scale=100).
   */
  displayScale?: number;
}

const CHANNELS: ChannelOption[] = [
  { id: 'ch1', label: 'Ch1' },
  { id: 'ch2', label: 'Ch2' },
  { id: 'ch3', label: 'Ch3' },
  { id: 'mono', label: 'Mono' },
];

const ASPECTS: AspectOption[] = [
  { id: '16x9', label: '16:9' },
  { id: '9x16', label: '9:16' },
  { id: '4x5', label: '4:5' },
  { id: 'ipad11_portrait', label: 'iPad 11' },
  { id: 'ipad13_portrait', label: 'iPad 13' },
];

const RESOLUTIONS: ResolutionOption[] = [
  { id: 'fhd', label: 'FHD' },
  { id: '4k', label: '4K' },
  { id: 'print', label: 'Print' },
];

const SLIDERS: SliderConfig[] = [
  { key: 'objectSize', label: 'Object Size', min: 0.15, max: 0.45, step: 0.01, decimals: 2 },
  { key: 'attraction', label: 'Attraction', min: 0.0, max: 0.1, step: 0.001, decimals: 3 },
];

const noopActions: ControlActions = {
  reset: () => {
    /* no-op until Wave 4 wires capture/world */
  },
  exportPng: async () => {
    /* no-op */
  },
  toggleRecording: async () => {
    /* no-op */
  },
  applyHueColors: () => {
    /* no-op */
  },
};

export function mountControls(root: HTMLElement, actions: ControlActions = noopActions): ControlsHandle {
  // ---- UI-local state (not in AppState) -------------------------------------
  let resolution: ResolutionPreset = 'fhd';
  let recording = false;
  let recordingTickHandle: number | null = null;
  let panelHidden = false;

  // ---- Build DOM (once) -----------------------------------------------------
  root.classList.add('kw-controls');
  root.replaceChildren(); // clear placeholder

  const panel = el('div', { class: 'kw-panel' });
  root.appendChild(panel);

  // Header
  const header = el('div', { class: 'kw-header' }, 'KANAWATSUGI VISUAL MACHINE');
  panel.appendChild(header);

  // ---- Frame toggle ---------------------------------------------------------
  // Flips the body background between paper-white (matches the canvas
  // clear colour, edge-to-edge feel for iPad/iPhone PWA) and the
  // legacy #d8d8d5 mid-gray (makes the aspect-letterbox bands visible
  // for desktop-web composition checks). Same single-toggle pattern
  // as Halftone below.
  const frameSection = el('section', { class: 'kw-section' });
  frameSection.appendChild(el('div', { class: 'kw-section-label' }, 'Frame'));
  const frameToggle = el('button', {
    class: 'kw-btn kw-btn-wide',
    type: 'button',
    'aria-pressed': 'false',
  }, 'OFF') as HTMLButtonElement;
  frameToggle.addEventListener('click', () => {
    store.set({ letterboxFrame: !store.get().letterboxFrame });
  });
  frameSection.appendChild(frameToggle);
  panel.appendChild(frameSection);

  // ---- Channel section ------------------------------------------------------
  const channelSection = el('section', { class: 'kw-section' });
  channelSection.appendChild(el('div', { class: 'kw-section-label' }, 'Channel'));
  const channelGrid = el('div', { class: 'kw-grid kw-grid-3' });
  const channelButtons = new Map<ChannelId, HTMLButtonElement>();
  for (const ch of CHANNELS) {
    const btn = el('button', {
      class: 'kw-btn',
      type: 'button',
      'data-channel': ch.id,
      'aria-pressed': 'false',
    }, ch.label) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      store.set({ channel: ch.id });
    });
    channelButtons.set(ch.id, btn);
    channelGrid.appendChild(btn);
  }
  channelSection.appendChild(channelGrid);

  // ---- Hue picker (Phase 3) ------------------------------------------------
  // Two hue sliders + swatch previews + Apply button, sitting inside
  // the Channel section. Hidden when Mono is active (hue is invisible
  // at C ≈ 0). The picker prefills from the seed-derived complementary
  // pair on every channel/seed change; user edits flow through the
  // store and only affect the next spawn until Apply is pressed.
  const hueBlock = el('div', { class: 'kw-hue-section' });

  const hueSliderA = el('input', {
    class: 'kw-hue-slider',
    type: 'range',
    min: '0',
    max: '360',
    step: '1',
    id: 'kw-hue-a',
    'aria-label': 'Hue A',
  }) as HTMLInputElement;
  const hueSwatchA = el('span', {
    class: 'kw-hue-swatch',
    'aria-hidden': 'true',
  }) as HTMLSpanElement;
  const hueRowA = el('div', { class: 'kw-hue-slider-row' });
  hueRowA.appendChild(el('label', { class: 'kw-hue-label', for: 'kw-hue-a' }, 'Hue A'));
  hueRowA.appendChild(hueSliderA);
  hueRowA.appendChild(hueSwatchA);
  hueBlock.appendChild(hueRowA);

  const hueSliderB = el('input', {
    class: 'kw-hue-slider',
    type: 'range',
    min: '0',
    max: '360',
    step: '1',
    id: 'kw-hue-b',
    'aria-label': 'Hue B',
  }) as HTMLInputElement;
  const hueSwatchB = el('span', {
    class: 'kw-hue-swatch',
    'aria-hidden': 'true',
  }) as HTMLSpanElement;
  const hueRowB = el('div', { class: 'kw-hue-slider-row' });
  hueRowB.appendChild(el('label', { class: 'kw-hue-label', for: 'kw-hue-b' }, 'Hue B'));
  hueRowB.appendChild(hueSliderB);
  hueRowB.appendChild(hueSwatchB);
  hueBlock.appendChild(hueRowB);

  const hueApplyBtn = el('button', {
    class: 'kw-btn kw-btn-wide kw-hue-apply-btn',
    type: 'button',
  }, 'Apply to existing blobs') as HTMLButtonElement;
  hueApplyBtn.addEventListener('click', () => {
    actions.applyHueColors();
  });
  hueBlock.appendChild(hueApplyBtn);
  channelSection.appendChild(hueBlock);

  hueSliderA.addEventListener('input', () => {
    const v = Number(hueSliderA.value);
    store.set({ hueA: v });
  });
  hueSliderB.addEventListener('input', () => {
    const v = Number(hueSliderB.value);
    store.set({ hueB: v });
  });

  panel.appendChild(channelSection);

  // ---- Aspect ---------------------------------------------------------------
  const aspectSection = el('section', { class: 'kw-section' });
  aspectSection.appendChild(el('div', { class: 'kw-section-label' }, 'Aspect'));
  const aspectGrid = el('div', { class: 'kw-grid kw-grid-3' });
  const aspectButtons = new Map<AspectId, HTMLButtonElement>();
  for (const a of ASPECTS) {
    const btn = el('button', {
      class: 'kw-btn',
      type: 'button',
      'data-aspect': a.id,
      'aria-pressed': 'false',
    }, a.label) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      store.set({ aspect: a.id });
    });
    aspectButtons.set(a.id, btn);
    aspectGrid.appendChild(btn);
  }
  aspectSection.appendChild(aspectGrid);
  panel.appendChild(aspectSection);

  // ---- Seed + Reset ---------------------------------------------------------
  const seedSection = el('section', { class: 'kw-section' });
  seedSection.appendChild(el('div', { class: 'kw-section-label' }, 'Seed'));
  const seedRow = el('div', { class: 'kw-seed-row' });
  const seedDisplay = el('span', {
    class: 'kw-seed',
    'aria-label': 'Current seed',
  }, store.get().seed) as HTMLSpanElement;
  const resetBtn = el('button', {
    class: 'kw-btn',
    type: 'button',
    'aria-label': 'Reset and re-roll seed',
  }, 'Reset') as HTMLButtonElement;
  resetBtn.addEventListener('click', () => {
    actions.reset();
  });
  seedRow.appendChild(seedDisplay);
  seedRow.appendChild(resetBtn);
  seedSection.appendChild(seedRow);
  panel.appendChild(seedSection);

  // ---- Parameters -----------------------------------------------------------
  const sliderInputs = new Map<SliderKey, HTMLInputElement>();
  const sliderReadouts = new Map<SliderKey, HTMLSpanElement>();
  const sliderConfigs = new Map<SliderKey, SliderConfig>();

  const buildSlider = (s: SliderConfig, host: HTMLElement): void => {
    sliderConfigs.set(s.key, s);
    const row = el('div', { class: 'kw-slider-row' });
    const label = el('label', { class: 'kw-slider-label', for: `kw-slider-${s.key}` }, s.label);
    const initial = (store.get() as AppState)[s.key] as number;
    const input = el('input', {
      class: 'kw-slider',
      type: 'range',
      id: `kw-slider-${s.key}`,
      min: String(s.min),
      max: String(s.max),
      step: String(s.step),
      value: String(initial),
    }) as HTMLInputElement;
    const readout = el(
      'span',
      { class: 'kw-slider-value' },
      formatSlider(initial, s),
    ) as HTMLSpanElement;
    input.addEventListener('input', () => {
      const v = Number(input.value);
      readout.textContent = formatSlider(v, s);
      store.set({ [s.key]: v } as Partial<AppState>);
    });
    sliderInputs.set(s.key, input);
    sliderReadouts.set(s.key, readout);
    row.appendChild(label);
    row.appendChild(input);
    row.appendChild(readout);
    host.appendChild(row);
  };

  const paramsSection = el('section', { class: 'kw-section' });
  paramsSection.appendChild(el('div', { class: 'kw-section-label' }, 'Parameters'));
  for (const s of SLIDERS) buildSlider(s, paramsSection);
  panel.appendChild(paramsSection);

  // ---- Background media (image or video, exclusive slot) -----------------
  const bgSection = el('section', { class: 'kw-section' });
  bgSection.appendChild(el('div', { class: 'kw-section-label' }, 'Background'));

  // Hidden file input — accepts both image and video MIMEs. The change
  // handler does the actual MIME-type discrimination and rejects
  // anything outside those two families.
  const bgFileInput = el('input', {
    class: 'kw-bg-file',
    type: 'file',
    accept: 'image/*,video/*',
    'aria-label': 'Choose background image or video',
  }) as HTMLInputElement;
  bgSection.appendChild(bgFileInput);

  // Choose / Remove row.
  const bgChooseRow = el('div', { class: 'kw-bg-choose-row' });
  const bgChooseBtn = el('button', {
    class: 'kw-btn kw-btn-wide',
    type: 'button',
  }, 'Choose Media') as HTMLButtonElement;
  bgChooseBtn.addEventListener('click', () => bgFileInput.click());
  bgChooseRow.appendChild(bgChooseBtn);
  bgSection.appendChild(bgChooseRow);

  // Preview row (thumbnail + filename + Remove button). The thumbnail
  // shows a still <img> for both image and video sources — we just
  // reuse the data URL for images, and skip the thumbnail for video
  // (filename only) to keep the panel quiet.
  const bgPreviewRow = el('div', { class: 'kw-bg-preview-row', hidden: 'true' });
  const bgThumb = el('img', {
    class: 'kw-bg-thumb',
    alt: 'Background thumbnail',
  }) as HTMLImageElement;
  const bgFilename = el('span', { class: 'kw-bg-filename' }) as HTMLSpanElement;
  const bgRemoveBtn = el('button', {
    class: 'kw-btn kw-btn-small',
    type: 'button',
  }, 'Remove') as HTMLButtonElement;
  bgRemoveBtn.addEventListener('click', () => {
    releaseMedia(store.get().bgMedia);
    store.set({ bgMedia: null, bgMediaEnabled: false });
    bgFileInput.value = '';
  });
  bgPreviewRow.appendChild(bgThumb);
  bgPreviewRow.appendChild(bgFilename);
  bgPreviewRow.appendChild(bgRemoveBtn);
  bgSection.appendChild(bgPreviewRow);

  // Opacity slider.
  buildSlider(
    {
      key: 'bgOpacity',
      label: 'Opacity',
      min: 0,
      max: 1,
      step: 0.01,
      decimals: 0,
      unit: '%',
      displayScale: 100,
    },
    bgSection,
  );

  // File-input change handler — branches on file.type, releases the
  // previously-bound media before swapping in the new source. The
  // exclusive-slot semantics are enforced here: every successful load
  // path calls `releaseMedia(prev)` first so we never leak object URLs
  // or leave a paused video element pinning a Blob.
  bgFileInput.addEventListener('change', () => {
    const file = bgFileInput.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      actions.notify?.('未対応のファイル形式です', 'error');
      bgFileInput.value = '';
      return;
    }

    if (isImage) {
      // Use object URLs (consistent with the video path) so we can
      // revoke them in `releaseMedia` without juggling FileReader
      // data URLs.
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        releaseMedia(store.get().bgMedia);
        store.set({ bgMedia: img, bgMediaEnabled: true });
        bgThumb.src = objectUrl;
        bgFilename.textContent = file.name;
        bgPreviewRow.removeAttribute('hidden');
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        actions.notify?.('画像の読み込みに失敗しました', 'error');
      };
      img.src = objectUrl;
      return;
    }

    // Video path. iPad-Safari kiosk-mode requirements:
    //   - muted=true + playsInline=true so autoplay is permitted
    //     without a prior user gesture.
    //   - loop=true so the loop runs forever for a passive viewer.
    //   - crossOrigin='anonymous' so a future captureStream() (Agent
    //     D) can read the frames without tainting the canvas.
    // The first user pointerdown on the canvas (see app.ts
    // handlePointerDown) flips muted=false to bring audio in.
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = objectUrl;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.crossOrigin = 'anonymous';
    const onCanPlay = (): void => {
      video.removeEventListener('canplay', onCanPlay);
      releaseMedia(store.get().bgMedia);
      store.set({ bgMedia: video, bgMediaEnabled: true });
      // Hide the still thumbnail for video — show only the filename.
      bgThumb.removeAttribute('src');
      bgFilename.textContent = file.name;
      bgPreviewRow.removeAttribute('hidden');
      video.play().catch((err) => {
        // Autoplay can still be blocked in some sandboxed contexts;
        // log but don't surface as an error since the first
        // pointerdown will retry play() too.
        // eslint-disable-next-line no-console
        console.warn('[bgMedia] video.play() rejected:', err);
      });
    };
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      actions.notify?.('動画の読み込みに失敗しました', 'error');
    });
    // Kick off load — autoplay attribute alone is sometimes ignored
    // for programmatically-created videos until load() is called.
    video.load();
  });

  panel.appendChild(bgSection);

  // ---- Halftone (Phase 4) --------------------------------------------------
  const halftoneSection = el('section', { class: 'kw-section' });
  halftoneSection.appendChild(el('div', { class: 'kw-section-label' }, 'Halftone'));

  // ON / OFF toggle (full-width, mirrors the old Film Noise toggle).
  const halftoneToggle = el('button', {
    class: 'kw-btn kw-btn-wide',
    type: 'button',
    'aria-pressed': 'false',
  }, 'OFF') as HTMLButtonElement;
  halftoneToggle.addEventListener('click', () => {
    store.set({ halftoneEnabled: !store.get().halftoneEnabled });
  });
  halftoneSection.appendChild(halftoneToggle);

  // Mode toggle (1 color / 2 color / Gray).
  const halftoneModeRow = el('div', { class: 'kw-resolution-row' });
  halftoneModeRow.appendChild(el('span', { class: 'kw-inline-label' }, 'Mode'));
  const halftoneModeGrid = el('div', { class: 'kw-grid kw-grid-3 kw-grid-compact' });
  const halftoneModeButtons = new Map<HalftoneMode, HTMLButtonElement>();
  const halftoneModeOptions: { id: HalftoneMode; label: string }[] = [
    { id: 'oneColor', label: '1 color' },
    { id: 'twoColor', label: '2 color' },
    { id: 'grayscale', label: 'Gray' },
  ];
  for (const o of halftoneModeOptions) {
    const btn = el('button', {
      class: 'kw-btn',
      type: 'button',
      'data-halftone-mode': o.id,
      'aria-pressed': 'false',
    }, o.label) as HTMLButtonElement;
    btn.addEventListener('click', () => store.set({ halftoneMode: o.id }));
    halftoneModeButtons.set(o.id, btn);
    halftoneModeGrid.appendChild(btn);
  }
  halftoneModeRow.appendChild(halftoneModeGrid);
  halftoneSection.appendChild(halftoneModeRow);

  // Halftone hue pickers — independent of the Channel pickers. Two
  // blocks: a single slider for 1-color mode, two sliders for 2-color
  // mode. Both hidden in grayscale mode.
  const halftoneHueOneBlock = el('div', { class: 'kw-hue-section' });
  const halftoneHueOneSlider = el('input', {
    class: 'kw-hue-slider',
    type: 'range',
    min: '0',
    max: '360',
    step: '1',
    id: 'kw-halftone-hue-one',
    'aria-label': 'Halftone Hue',
  }) as HTMLInputElement;
  const halftoneHueOneSwatch = el('span', {
    class: 'kw-hue-swatch',
    'aria-hidden': 'true',
  }) as HTMLSpanElement;
  const halftoneHueOneRow = el('div', { class: 'kw-hue-slider-row' });
  halftoneHueOneRow.appendChild(
    el('label', { class: 'kw-hue-label', for: 'kw-halftone-hue-one' }, 'Hue'),
  );
  halftoneHueOneRow.appendChild(halftoneHueOneSlider);
  halftoneHueOneRow.appendChild(halftoneHueOneSwatch);
  halftoneHueOneBlock.appendChild(halftoneHueOneRow);
  halftoneHueOneSlider.addEventListener('input', () => {
    store.set({ halftoneHueOne: Number(halftoneHueOneSlider.value) });
  });
  halftoneSection.appendChild(halftoneHueOneBlock);

  const halftoneHueTwoBlock = el('div', { class: 'kw-hue-section' });
  const halftoneHueTwoASlider = el('input', {
    class: 'kw-hue-slider',
    type: 'range',
    min: '0',
    max: '360',
    step: '1',
    id: 'kw-halftone-hue-two-a',
    'aria-label': 'Halftone Hue A',
  }) as HTMLInputElement;
  const halftoneHueTwoASwatch = el('span', {
    class: 'kw-hue-swatch',
    'aria-hidden': 'true',
  }) as HTMLSpanElement;
  const halftoneHueTwoARow = el('div', { class: 'kw-hue-slider-row' });
  halftoneHueTwoARow.appendChild(
    el('label', { class: 'kw-hue-label', for: 'kw-halftone-hue-two-a' }, 'Hue A'),
  );
  halftoneHueTwoARow.appendChild(halftoneHueTwoASlider);
  halftoneHueTwoARow.appendChild(halftoneHueTwoASwatch);
  halftoneHueTwoBlock.appendChild(halftoneHueTwoARow);

  const halftoneHueTwoBSlider = el('input', {
    class: 'kw-hue-slider',
    type: 'range',
    min: '0',
    max: '360',
    step: '1',
    id: 'kw-halftone-hue-two-b',
    'aria-label': 'Halftone Hue B',
  }) as HTMLInputElement;
  const halftoneHueTwoBSwatch = el('span', {
    class: 'kw-hue-swatch',
    'aria-hidden': 'true',
  }) as HTMLSpanElement;
  const halftoneHueTwoBRow = el('div', { class: 'kw-hue-slider-row' });
  halftoneHueTwoBRow.appendChild(
    el('label', { class: 'kw-hue-label', for: 'kw-halftone-hue-two-b' }, 'Hue B'),
  );
  halftoneHueTwoBRow.appendChild(halftoneHueTwoBSlider);
  halftoneHueTwoBRow.appendChild(halftoneHueTwoBSwatch);
  halftoneHueTwoBlock.appendChild(halftoneHueTwoBRow);

  halftoneHueTwoASlider.addEventListener('input', () => {
    store.set({ halftoneHueTwoA: Number(halftoneHueTwoASlider.value) });
  });
  halftoneHueTwoBSlider.addEventListener('input', () => {
    store.set({ halftoneHueTwoB: Number(halftoneHueTwoBSlider.value) });
  });
  halftoneSection.appendChild(halftoneHueTwoBlock);

  // Shape toggle (circle / square / diamond / triangle). Unicode
  // glyphs as the visible label (compact, language-neutral) with
  // aria-label carrying the spelt-out name for screen readers.
  const halftoneShapeRow = el('div', { class: 'kw-resolution-row' });
  halftoneShapeRow.appendChild(el('span', { class: 'kw-inline-label' }, 'Shape'));
  const halftoneShapeGrid = el('div', { class: 'kw-grid kw-grid-5 kw-grid-compact' });
  const halftoneShapeButtons = new Map<HalftoneShape, HTMLButtonElement>();
  const halftoneShapeOptions: { id: HalftoneShape; glyph: string; label: string }[] = [
    { id: 'circle', glyph: '○', label: 'Circle' },
    { id: 'square', glyph: '□', label: 'Square' },
    { id: 'diamond', glyph: '◇', label: 'Diamond' },
    { id: 'triangle', glyph: '△', label: 'Triangle' },
    { id: 'plus', glyph: '＋', label: 'Plus' },
  ];
  for (const o of halftoneShapeOptions) {
    const btn = el('button', {
      class: 'kw-btn kw-halftone-shape-btn',
      type: 'button',
      'data-halftone-shape': o.id,
      'aria-pressed': 'false',
      'aria-label': o.label,
      title: o.label,
    }, o.glyph) as HTMLButtonElement;
    btn.addEventListener('click', () => store.set({ halftoneShape: o.id }));
    halftoneShapeButtons.set(o.id, btn);
    halftoneShapeGrid.appendChild(btn);
  }
  halftoneShapeRow.appendChild(halftoneShapeGrid);
  halftoneSection.appendChild(halftoneShapeRow);

  // Size slider (cell-to-cell distance in FHD-CSS px).
  buildSlider(
    {
      key: 'halftoneCellSize',
      label: 'Size',
      min: 4,
      max: 48,
      step: 1,
      decimals: 0,
      unit: 'px',
    },
    halftoneSection,
  );

  // Gap slider (dot radius cap as fraction of cell inradius — controls
  // how far apart dots breathe from their neighbours).
  buildSlider(
    {
      key: 'halftoneGap',
      label: 'Gap',
      min: 0.3,
      max: 1.0,
      step: 0.01,
      decimals: 2,
    },
    halftoneSection,
  );

  panel.appendChild(halftoneSection);

  // ---- Typography (Phase 8) -------------------------------------------------
  // Three editable text fields (title / date / time) and a horizontal/
  // vertical title-axis toggle. Edits flow into the store on every
  // keystroke; app.ts re-composes the overlay on the resulting
  // state-change tick.
  const typographySection = el('section', { class: 'kw-section' });
  typographySection.appendChild(el('div', { class: 'kw-section-label' }, 'Typography'));

  const buildTextRow = (
    key: 'dateText' | 'timeText',
    label: string,
  ): HTMLInputElement => {
    const row = el('div', { class: 'kw-text-row' });
    const id = `kw-text-${key}`;
    row.appendChild(el('label', { class: 'kw-text-label', for: id }, label));
    const input = el('input', {
      class: 'kw-text-input',
      type: 'text',
      id,
      value: store.get()[key],
      'aria-label': label,
    }) as HTMLInputElement;
    input.addEventListener('input', () => {
      store.set({ [key]: input.value } as Partial<AppState>);
    });
    row.appendChild(input);
    typographySection.appendChild(row);
    return input;
  };

  /**
   * Title is a multi-line textarea so the user can author a layout
   * with a `\n` break (e.g. "カナ\nワツギ"). The textarea auto-grows
   * as new lines are typed, capped at MAX_TITLE_ROWS so the panel
   * doesn't run away vertically — beyond that the field falls back
   * to internal scroll. Uses the same `.kw-text-input` look as the
   * single-line metadata fields, with `.kw-text-input--multi`
   * adding `resize: none` and `overflow-y: auto`.
   *
   * placeholder is "カナワツギ" so an empty field still hints at the
   * canonical title.
   */
  const MAX_TITLE_ROWS = 4;
  // Approximate per-row pixel height (matches typography.css's 1.4
  // line-height × 11px font-size, with a small allowance for vertical
  // padding). Recomputed on every input so a webfont-load mid-edit
  // can't drift the cap badly.
  const titleRow = el('div', { class: 'kw-text-row' });
  titleRow.appendChild(
    el('label', { class: 'kw-text-label', for: 'kw-text-titleText' }, 'Title'),
  );
  const titleTextInput = el('textarea', {
    class: 'kw-text-input kw-text-input--multi',
    id: 'kw-text-titleText',
    rows: '2',
    placeholder: 'カナワツギ',
    'aria-label': 'Title',
  }) as unknown as HTMLTextAreaElement;
  titleTextInput.value = store.get().titleText;

  const resizeTitleTextarea = (): void => {
    // `auto` first lets scrollHeight measure the natural content
    // height; without the reset the field would only ever grow.
    titleTextInput.style.height = 'auto';
    const lineHeightPx = parseFloat(getComputedStyle(titleTextInput).lineHeight);
    const cap = Number.isFinite(lineHeightPx) && lineHeightPx > 0
      ? lineHeightPx * MAX_TITLE_ROWS
      : 16 * MAX_TITLE_ROWS;
    titleTextInput.style.height = `${Math.min(titleTextInput.scrollHeight, cap)}px`;
  };

  titleTextInput.addEventListener('input', () => {
    store.set({ titleText: titleTextInput.value });
    resizeTitleTextarea();
  });
  titleRow.appendChild(titleTextInput);
  typographySection.appendChild(titleRow);
  // Initial sizing — required so the field starts at the right height
  // for a multi-line preset; `rows="2"` alone would only set the
  // minimum, not the post-content height.
  // Defer to a microtask so getComputedStyle sees the mounted styles.
  queueMicrotask(resizeTitleTextarea);

  const dateTextInput = buildTextRow('dateText', 'Date');
  const timeTextInput = buildTextRow('timeText', 'Time');

  // Orientation toggle — same button-group pattern as Channel / Aspect.
  const orientationRow = el('div', { class: 'kw-resolution-row' });
  orientationRow.appendChild(el('span', { class: 'kw-inline-label' }, 'Axis'));
  const orientationGrid = el('div', { class: 'kw-grid kw-grid-2 kw-grid-compact' });
  const orientationButtons = new Map<TitleOrientation, HTMLButtonElement>();
  const orientationOptions: { id: TitleOrientation; label: string }[] = [
    { id: 'horizontal', label: '横' },
    { id: 'vertical', label: '縦' },
  ];
  for (const o of orientationOptions) {
    const btn = el('button', {
      class: 'kw-btn',
      type: 'button',
      'data-title-orientation': o.id,
      'aria-pressed': 'false',
      'aria-label': o.id === 'horizontal' ? 'Horizontal title' : 'Vertical title',
    }, o.label) as HTMLButtonElement;
    btn.addEventListener('click', () => store.set({ titleOrientation: o.id }));
    orientationButtons.set(o.id, btn);
    orientationGrid.appendChild(btn);
  }
  orientationRow.appendChild(orientationGrid);
  typographySection.appendChild(orientationRow);

  // Title Size slider — same pattern as Object Size / Attraction in
  // the Parameters section. The state key already exists; it just had
  // no UI. Range and step are art-direction picks: 24..160 px covers
  // the realistic span of title sizes against a 1080-tall preview, and
  // 1px steps give fine enough control without a numeric input.
  buildSlider(
    {
      key: 'titleSize',
      label: 'Title Size',
      min: 24,
      max: 160,
      step: 1,
      decimals: 0,
    },
    typographySection,
  );

  panel.appendChild(typographySection);

  // ---- Export ---------------------------------------------------------------
  const exportSection = el('section', { class: 'kw-section' });
  exportSection.appendChild(el('div', { class: 'kw-section-label' }, 'Export'));

  // Resolution row
  const resRow = el('div', { class: 'kw-resolution-row' });
  const resLabel = el('span', { class: 'kw-inline-label' }, 'Resolution');
  const resGrid = el('div', { class: 'kw-grid kw-grid-3 kw-grid-compact' });
  const resButtons = new Map<ResolutionPreset, HTMLButtonElement>();
  for (const r of RESOLUTIONS) {
    const btn = el('button', {
      class: 'kw-btn',
      type: 'button',
      'data-resolution': r.id,
      'aria-pressed': r.id === resolution ? 'true' : 'false',
    }, r.label) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      resolution = r.id;
      updateResolutionButtons();
    });
    resButtons.set(r.id, btn);
    resGrid.appendChild(btn);
  }
  resRow.appendChild(resLabel);
  resRow.appendChild(resGrid);
  exportSection.appendChild(resRow);

  // Export PNG button
  const exportPngBtn = el('button', {
    class: 'kw-btn kw-btn-wide',
    type: 'button',
  }, 'Export PNG') as HTMLButtonElement;
  exportPngBtn.addEventListener('click', () => {
    void runExportPng();
  });
  exportSection.appendChild(exportPngBtn);

  // Record WebM button
  const recordBtn = el('button', {
    class: 'kw-btn kw-btn-wide kw-btn-record',
    type: 'button',
    'aria-pressed': 'false',
  }) as HTMLButtonElement;
  const recordIcon = el('span', { class: 'kw-record-icon', 'aria-hidden': 'true' }, '●');
  const recordLabel = el('span', { class: 'kw-record-label' }, 'Record WebM');
  recordBtn.appendChild(recordIcon);
  recordBtn.appendChild(recordLabel);
  recordBtn.addEventListener('click', () => {
    void runToggleRecording();
  });
  exportSection.appendChild(recordBtn);

  // Recording indicator (hidden by default)
  const recordIndicator = el('div', { class: 'kw-record-indicator', hidden: 'true' });
  const recordDot = el('span', { class: 'kw-record-dot', 'aria-hidden': 'true' }, '●');
  const recordTime = el('span', { class: 'kw-record-time' }, '0:00');
  recordIndicator.appendChild(recordDot);
  recordIndicator.appendChild(recordTime);
  exportSection.appendChild(recordIndicator);

  panel.appendChild(exportSection);

  // ---- Footer hint ----------------------------------------------------------
  // ---- Hide UI section ------------------------------------------------------
  // Single button that hides the panel and requests fullscreen in one
  // action. The hairline divider above echoes the panel-border / section
  // -divider colour (#e5e5e5) elsewhere; the 12px margin is provisional
  // and may need art-direction tuning.
  const hideUiSection = el('section', { class: 'kw-section kw-hide-ui-section' });
  const hideUiButton = el('button', {
    class: 'kw-btn kw-btn-wide',
    type: 'button',
  }, 'Hide UI') as HTMLButtonElement;
  hideUiButton.addEventListener('click', () => {
    setPanelHidden(true);
    // Expand the canvas to fill the viewport (bypass aspect letterbox).
    // Capture pipelines (PNG/WebM) ignore this and continue using
    // state.aspect, so exports remain aspect-locked.
    store.set({ isFullscreenMode: true });
    try {
      const p = document.documentElement.requestFullscreen?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      // Fallback for browsers without fullscreen API (e.g. older Safari):
      // panel still hides; user can press H to restore.
    }
  });
  hideUiSection.appendChild(hideUiButton);
  panel.appendChild(hideUiSection);

  // ---- Reactive sync from store --------------------------------------------
  const renderFromStore = (s: AppState): void => {
    // Channel
    for (const [id, btn] of channelButtons) {
      const active = id === s.channel;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    // Aspect
    for (const [id, btn] of aspectButtons) {
      const active = id === s.aspect;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    // Seed
    seedDisplay.textContent = s.seed;
    // Background preview row — visible iff a bg media is loaded.
    if (s.bgMedia === null) {
      bgPreviewRow.setAttribute('hidden', 'true');
      if (bgThumb.src !== '') bgThumb.removeAttribute('src');
      bgFilename.textContent = '';
    }
    // Sliders — only update if not currently focused (avoid stomping drag)
    for (const [key, input] of sliderInputs) {
      const readout = sliderReadouts.get(key);
      const conf = sliderConfigs.get(key);
      if (!readout || !conf) continue;
      const value = (s as AppState)[key] as number;
      if (document.activeElement !== input && Number(input.value) !== value) {
        input.value = String(value);
      }
      readout.textContent = formatSlider(value, conf);
    }

    // Hue picker — track gradient, slider value, swatch preview, and
    // visibility (hidden for Mono since hue is invisible at C ≈ 0).
    const hueLC = CHANNEL_HUE_TRACK[s.channel];
    if (s.channel === 'mono') {
      hueBlock.setAttribute('hidden', 'true');
    } else {
      hueBlock.removeAttribute('hidden');
    }
    const hueTrack = buildHueTrackGradient(hueLC.l, hueLC.c);
    hueSliderA.style.setProperty('--hue-track', hueTrack);
    hueSliderB.style.setProperty('--hue-track', hueTrack);
    if (document.activeElement !== hueSliderA && Number(hueSliderA.value) !== Math.round(s.hueA)) {
      hueSliderA.value = String(Math.round(s.hueA));
    }
    if (document.activeElement !== hueSliderB && Number(hueSliderB.value) !== Math.round(s.hueB)) {
      hueSliderB.value = String(Math.round(s.hueB));
    }
    hueSwatchA.style.background = oklchCss(hueLC.l, hueLC.c, s.hueA);
    hueSwatchB.style.background = oklchCss(hueLC.l, hueLC.c, s.hueB);

    // Halftone — ON/OFF, mode toggle, angle toggle. The Size slider is
    // synced through the generic slider machinery above.
    halftoneToggle.textContent = s.halftoneEnabled ? 'ON' : 'OFF';
    halftoneToggle.classList.toggle('is-active', s.halftoneEnabled);
    halftoneToggle.setAttribute('aria-pressed', s.halftoneEnabled ? 'true' : 'false');

    frameToggle.textContent = s.letterboxFrame ? 'ON' : 'OFF';
    frameToggle.classList.toggle('is-active', s.letterboxFrame);
    frameToggle.setAttribute('aria-pressed', s.letterboxFrame ? 'true' : 'false');
    for (const [id, btn] of halftoneModeButtons) {
      const active = id === s.halftoneMode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    for (const [id, btn] of halftoneShapeButtons) {
      const active = id === s.halftoneShape;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    // Halftone hue pickers — visibility per mode; track gradient and
    // swatches use the active channel's L/C (the same `hueLC` used by
    // the Channel pickers above).
    if (s.halftoneMode === 'oneColor') {
      halftoneHueOneBlock.removeAttribute('hidden');
      halftoneHueTwoBlock.setAttribute('hidden', 'true');
    } else if (s.halftoneMode === 'twoColor') {
      halftoneHueOneBlock.setAttribute('hidden', 'true');
      halftoneHueTwoBlock.removeAttribute('hidden');
    } else {
      halftoneHueOneBlock.setAttribute('hidden', 'true');
      halftoneHueTwoBlock.setAttribute('hidden', 'true');
    }
    halftoneHueOneSlider.style.setProperty('--hue-track', hueTrack);
    halftoneHueTwoASlider.style.setProperty('--hue-track', hueTrack);
    halftoneHueTwoBSlider.style.setProperty('--hue-track', hueTrack);
    if (
      document.activeElement !== halftoneHueOneSlider
      && Number(halftoneHueOneSlider.value) !== Math.round(s.halftoneHueOne)
    ) {
      halftoneHueOneSlider.value = String(Math.round(s.halftoneHueOne));
    }
    if (
      document.activeElement !== halftoneHueTwoASlider
      && Number(halftoneHueTwoASlider.value) !== Math.round(s.halftoneHueTwoA)
    ) {
      halftoneHueTwoASlider.value = String(Math.round(s.halftoneHueTwoA));
    }
    if (
      document.activeElement !== halftoneHueTwoBSlider
      && Number(halftoneHueTwoBSlider.value) !== Math.round(s.halftoneHueTwoB)
    ) {
      halftoneHueTwoBSlider.value = String(Math.round(s.halftoneHueTwoB));
    }
    halftoneHueOneSwatch.style.background = oklchCss(hueLC.l, hueLC.c, s.halftoneHueOne);
    halftoneHueTwoASwatch.style.background = oklchCss(hueLC.l, hueLC.c, s.halftoneHueTwoA);
    halftoneHueTwoBSwatch.style.background = oklchCss(hueLC.l, hueLC.c, s.halftoneHueTwoB);

    // Typography text inputs — only update if the field isn't focused
    // so we don't stomp the user's caret mid-edit.
    if (document.activeElement !== titleTextInput && titleTextInput.value !== s.titleText) {
      titleTextInput.value = s.titleText;
      // Re-fit the textarea height to the externally-set value.
      resizeTitleTextarea();
    }
    if (document.activeElement !== dateTextInput && dateTextInput.value !== s.dateText) {
      dateTextInput.value = s.dateText;
    }
    if (document.activeElement !== timeTextInput && timeTextInput.value !== s.timeText) {
      timeTextInput.value = s.timeText;
    }
    // Orientation toggle.
    for (const [id, btn] of orientationButtons) {
      const active = id === s.titleOrientation;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  };

  renderFromStore(store.get());
  const unsubscribe = store.subscribe((next) => renderFromStore(next));

  // ---- Helpers --------------------------------------------------------------
  function updateResolutionButtons(): void {
    for (const [id, btn] of resButtons) {
      const active = id === resolution;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  async function runExportPng(): Promise<void> {
    if (exportPngBtn.disabled) return;
    const originalLabel = exportPngBtn.textContent ?? 'Export PNG';
    exportPngBtn.disabled = true;
    exportPngBtn.classList.add('is-busy');
    exportPngBtn.textContent = 'Exporting…';
    try {
      await actions.exportPng();
    } catch (err) {
      actions.notify?.(`PNG export failed: ${(err as Error).message}`, 'error');
    } finally {
      exportPngBtn.disabled = false;
      exportPngBtn.classList.remove('is-busy');
      exportPngBtn.textContent = originalLabel;
    }
  }

  async function runToggleRecording(): Promise<void> {
    if (recordBtn.disabled) return;
    recordBtn.disabled = true;
    try {
      await actions.toggleRecording();
    } catch (err) {
      actions.notify?.(`Recording toggle failed: ${(err as Error).message}`, 'error');
    } finally {
      recordBtn.disabled = false;
    }
  }

  function applyRecordingState(): void {
    if (recording) {
      recordBtn.classList.add('is-recording');
      recordBtn.setAttribute('aria-pressed', 'true');
      recordIcon.textContent = '■'; // square
      recordLabel.textContent = 'Stop';
      recordIndicator.removeAttribute('hidden');
      tickRecordTime();
      if (recordingTickHandle === null) {
        recordingTickHandle = window.setInterval(tickRecordTime, 500);
      }
    } else {
      recordBtn.classList.remove('is-recording');
      recordBtn.setAttribute('aria-pressed', 'false');
      recordIcon.textContent = '●';
      recordLabel.textContent = 'Record WebM';
      recordIndicator.setAttribute('hidden', 'true');
      if (recordingTickHandle !== null) {
        window.clearInterval(recordingTickHandle);
        recordingTickHandle = null;
      }
      recordTime.textContent = '0:00';
    }
  }

  function tickRecordTime(): void {
    const seconds = actions.getRecordingElapsedSeconds?.() ?? 0;
    recordTime.textContent = formatElapsed(seconds);
  }

  // ---- Panel hide/show ------------------------------------------------------
  // Single source of truth for toggling the panel's visibility — used
  // by the H-key shortcut, the Hide UI button, and the fullscreenchange
  // listener (which auto-restores the panel when fullscreen exits).
  function setPanelHidden(hidden: boolean): void {
    panelHidden = hidden;
    panel.classList.toggle('is-hidden', panelHidden);
  }

  // ---- Keyboard: H toggles panel -------------------------------------------
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'h' && e.key !== 'H') return;
    const t = e.target as HTMLElement | null;
    if (t) {
      const tag = t.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        t.isContentEditable === true;
      if (isEditable) return;
    }
    e.preventDefault();
    setPanelHidden(!panelHidden);
  };
  window.addEventListener('keydown', onKeyDown);

  // ---- Fullscreen sync ------------------------------------------------------
  // When the user exits fullscreen (Esc, iPad top-edge swipe, etc.),
  // automatically restore the panel. The Hide UI button is the only
  // path that *enters* fullscreen, so this gives a clean round-trip.
  // Fullscreen entered via the button hides the panel directly; we
  // don't react to enter events here.
  const onFullscreenChange = (): void => {
    if (document.fullscreenElement === null) {
      setPanelHidden(false);
      // Restore the aspect-letterboxed canvas preview.
      store.set({ isFullscreenMode: false });
    }
  };
  document.addEventListener('fullscreenchange', onFullscreenChange);

  // ---- Handle ---------------------------------------------------------------
  const handle: ControlsHandle = {
    unmount(): void {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      unsubscribe();
      if (recordingTickHandle !== null) {
        window.clearInterval(recordingTickHandle);
        recordingTickHandle = null;
      }
      root.replaceChildren();
      root.classList.remove('kw-controls');
    },
    getResolution(): ResolutionPreset {
      return resolution;
    },
    setRecording(value: boolean): void {
      if (recording === value) return;
      recording = value;
      applyRecordingState();
    },
  };
  return handle;
}

// ============================================================================
// Media helpers
// ============================================================================

/**
 * Tear down a previously-bound bg media source. Idempotent on `null`.
 * Called before swapping in a new source and on Remove. Revokes the
 * object URL backing the source so the underlying File blob can be
 * released, and pauses + clears the <video> element's src so the
 * decoder thread releases its lock.
 *
 * Both branches set `src = ''` after revoking to drop the stale
 * reference; for video we additionally call `load()` so the element
 * fully detaches from the old source (some browsers keep the decoder
 * alive on the old URL until a fresh load).
 */
function releaseMedia(media: HTMLImageElement | HTMLVideoElement | null): void {
  if (media === null) return;
  const src = media.src;
  if (media instanceof HTMLVideoElement) {
    try {
      media.pause();
    } catch {
      /* pause() can throw if already detached; ignore. */
    }
    if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
    media.removeAttribute('src');
    try {
      media.load();
    } catch {
      /* load() likewise can throw on a half-torn-down element. */
    }
  } else {
    if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
    media.removeAttribute('src');
  }
}

// ============================================================================
// DOM helpers
// ============================================================================

type Attrs = Record<string, string>;

function el(tag: string, attrs?: Attrs, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (k === 'class') node.className = v;
      else node.setAttribute(k, v);
    }
  }
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatNumber(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

function formatSlider(value: number, conf: SliderConfig): string {
  const scaled = value * (conf.displayScale ?? 1);
  const text = formatNumber(scaled, conf.decimals);
  return conf.unit ? `${text}${conf.unit}` : text;
}

function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * CSS `oklch()` formatter. Browser support is Baseline 2023 (Chrome
 * 113+, Firefox 113+, Safari 15.4+); used for hue slider tracks and
 * swatches so the colour the user sees in the UI matches the colour
 * the shader will render.
 */
function oklchCss(l: number, c: number, hDeg: number): string {
  return `oklch(${(l * 100).toFixed(2)}% ${c.toFixed(4)} ${hDeg.toFixed(1)}deg)`;
}

/**
 * 13-stop linear gradient (every 30° from 0 to 360) for a hue slider
 * track at the channel's preset L/C. Stops are interpolated by the
 * browser in sRGB by default — perceptually OK at 12 segments × 30°,
 * but if banding becomes visible (especially around the yellow→green
 * boundary at hue 90°–150°) bump the stop count up.
 */
function buildHueTrackGradient(l: number, c: number): string {
  const stops: string[] = [];
  for (let i = 0; i <= 12; i++) {
    const h = i * 30;
    const pct = ((i / 12) * 100).toFixed(2);
    stops.push(`${oklchCss(l, c, h)} ${pct}%`);
  }
  return `linear-gradient(to right, ${stops.join(', ')})`;
}
